<template>
  <div :class="$style.Container">
    <div v-if="qrcodeLoading" :class="$style.SkeletonImage">
      <img src="https://static.lingvisions.com/public/1433d18c7b7a41188660ac703319f0da.svg" />
    </div>
    <div :class="$style.Error" v-else-if="qrcodeError" @click="start">
      <div>出错了</div>
      <Button>重试</Button>
    </div>
    <div :class="$style.Qrcode" v-else>
      <QrcodeVue :value="payUrl" :margin="2" :size="120" />
    </div>
    <div :class="$style.End">
      <div :class="$style.Price">
        <span>支付金额:</span>
        <span>
          <span style="font-size: 24px">￥</span>
          {{ qrcodeLoading || qrcodeError ? '...' : `${price}` }}
        </span>
        <img
          v-if="props.first"
          style="width: 107px; height: 18px; margin-left: 16px; margin-bottom: 7px"
          src="https://static.lingvisions.com/public/a0216dcc9b13401a9d00a30bcc923825.svg"
        />
      </div>
      <div :class="$style.PayType">
        <img src="https://static.lingvisions.com/public/02bc0157692b4a1a8db3e965cd08a678.png" />
        <img src="https://static.lingvisions.com/public/b3566a3c6dcf43d9b9db939e904a0618.png" />
        支付宝/微信扫码支付
      </div>
      <div :class="$style.Agreement">
        支付即视为您已同意
        <Protocol :isActiveVipTab="props.isActiveVipTab" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onUnmounted, onMounted, computed } from 'vue';
  import { getApi, getEnv } from '../init';
  import { SkeletonImage, Button } from 'ant-design-vue';
  import Protocol from './protocol.vue';
  import to from 'await-to-js';
  import QrcodeVue from 'qrcode.vue';
  const props = defineProps({
    skuId: {
      type: Number,
      default: null,
    },

    // 用于获取待支付订单code，没传则是新订单
    tradeNo: {
      type: String,
      default: null,
    },

    isActiveVipTab: {
      type: Boolean,
      default: false,
    },

    first: {
      type: Boolean,
      default: false,
    },

    qrcodeApiParams: {
      type: Object,
      default: null,
    },
  });

  const emits = defineEmits(['success']);

  const qrcodeLoading = ref(true);
  const qrcodeError = ref(false);
  const payCode = ref();
  const price = ref();
  const env = getEnv();

  const payUrl = computed(() => {
    const hostMap = {
      local: 'https://chore-dev.lingvisions.com',
      development: 'https://chore-dev.lingvisions.com',
      fat: 'https://chore-fat.lingvisions.com',
      production: 'https://www.ipoddy.cn',
    };
    return hostMap[env] + '/single/pay/' + payCode.value;
  });

  let timer;

  async function polling() {
    const result = await getApi().tcQrCodeApi.getTransactionCode({
      code: payCode.value,
    });

    if (result.status === 'success') {
      end();
      emits('success', result);
    }
  }

  // 获取价格
  async function getPrice() {
    const result = await getApi().aiadTransactionsApi.getMagicBeanSkuPrice({
      skuId: props.skuId,
    });
    price.value = result.price;
  }

  // 获取支付code
  async function getPayCode() {
    const params = props.qrcodeApiParams || {};
    const result = await getApi().tcQrCodeApi.transactionCode({
      skuId: props.tradeNo ? undefined : props.skuId,
      tradeNo: props.tradeNo || undefined,
      ...params,
    });
    payCode.value = result;
  }

  async function start() {
    qrcodeError.value = false;
    qrcodeLoading.value = true;

    const [err] = await to(Promise.all([getPrice(), getPayCode()]));

    if (err) {
      qrcodeError.value = true;
    }
    qrcodeLoading.value = false;

    if (payCode.value) {
      timer = setInterval(() => {
        polling();
      }, 1000);
    }
  }

  function end() {
    clearInterval(timer);
  }

  onMounted(() => {
    if (props.skuId) {
      start();
    }
  });

  onUnmounted(() => {
    end();
  });
</script>

<style lang="less" module>
  .Container {
    display: flex;
  }

  .Qrcode {
    width: 120px;
    height: 120px;

    canvas {
      user-select: none;
    }
  }

  .Error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
    border: 1px solid #fff;
  }

  .SkeletonImage {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 120px !important;
    height: 120px !important;
    background-color: rgba(66, 66, 66, 1);

    > img {
      width: 32px;
      height: 32px;
    }
  }

  .End {
    margin-left: 20px;
  }

  .Price {
    display: flex;
    align-items: flex-end;
    margin-top: 12px;

    > span {
      &:first-of-type {
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        color: rgba(255, 255, 255, 0.85);
      }

      &:last-of-type {
        font-weight: 600;
        font-size: 32px;
        line-height: 32px;
        color: rgba(209, 108, 68, 1);
        margin-left: 3px;
        letter-spacing: -1px;
      }
    }
  }

  .PayType {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.85);
    margin-top: 18px;

    > img {
      width: 16px;
      height: 16px;
      object-fit: contain;
      margin-right: 4px;

      &:last-of-type {
        margin-right: 8px;
      }
    }
  }

  .Agreement {
    display: flex;
    align-items: center;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.85);
  }
</style>
